import { Component, OnInit } from '@angular/core';
import { NewsletterService } from './newsletter.service';
import { NewsletterSubscription } from './newsletter.model';
import { NotificationService } from '../../core/notification.service';

@Component({
  standalone: false,
  selector: 'app-newsletter-list',
  template: `
    <div class="bg-white p-4 rounded shadow">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">Inscrições na Newsletter</h2>
        <div class="flex gap-2">
          <button (click)="setFilter(true)" [class]="filterBtnClass(true)" class="px-3 py-1 text-sm rounded border">Ativos</button>
          <button (click)="setFilter(false)" [class]="filterBtnClass(false)" class="px-3 py-1 text-sm rounded border">Inativos</button>
          <button (click)="setFilter(undefined)" [class]="filterBtnClass(undefined)" class="px-3 py-1 text-sm rounded border">Todos</button>
          <button (click)="load()" class="px-3 py-1 text-sm rounded border">Atualizar</button>
        </div>
      </div>

      <div *ngIf="loading" class="text-center py-8 text-muted">Carregando...</div>

      <div *ngIf="!loading && items.length === 0" class="text-center py-8 text-muted">
        Nenhuma inscrição encontrada.
      </div>

      <div *ngIf="!loading && items.length > 0" class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 border-b">
            <tr>
              <th *ngFor="let col of columns" class="text-left p-3 font-medium">
                {{ headerLabel(col) }}
              </th>
              <th class="text-right p-3 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of items" class="border-b hover:bg-gray-50">
              <td *ngFor="let col of columns" class="p-3 text-muted">
                <ng-container [ngSwitch]="col">
                  <span *ngSwitchCase="'email'" class="font-mono text-xs">{{ formatValue(item, col) }}</span>
                  <span *ngSwitchCase="'isActive'" [class]="item.isActive ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'" class="px-2 py-1 rounded text-xs font-medium">
                    {{ formatValue(item, col) }}
                  </span>
                  <span *ngSwitchDefault>{{ formatValue(item, col) }}</span>
                </ng-container>
              </td>
              <td class="p-3 text-right whitespace-nowrap">
                <button *ngIf="item.isActive" (click)="unsubscribe(item)" class="text-xs px-2 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50">
                  Desinscrever
                </button>
                <button *ngIf="!item.isActive" (click)="resubscribe(item)" class="text-xs px-2 py-1 text-green-600 border border-green-300 rounded hover:bg-green-50">
                  Reativar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div *ngIf="totalPages > 1" class="mt-4 flex items-center justify-between">
        <div class="text-sm text-muted">
          Página {{ page }} de {{ totalPages }} • Total: {{ total }} inscrições
        </div>
        <div class="flex gap-2">
          <button (click)="prev()" [disabled]="page === 1" class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            Anterior
          </button>
          <button (click)="next()" [disabled]="page >= totalPages" class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            Próxima
          </button>
        </div>
      </div>
    </div>
  `
})
export class NewsletterListComponent implements OnInit {
  items: NewsletterSubscription[] = [];
  loading = false;
  page = 1;
  pageSize = 20;
  totalPages = 1;
  total = 0;
  isActiveFilter: boolean | undefined = true;
  columns: string[] = ['email', 'isActive', 'subscribedAt', 'unsubscribedAt'];

  private headerMap: Record<string, string> = {
    email: 'Email',
    nome: 'Nome',
    name: 'Nome',
    firstName: 'Nome',
    lastName: 'Sobrenome',
    phone: 'Telefone',
    telefone: 'Telefone',
    celular: 'Celular',
    cidade: 'Cidade',
    estado: 'Estado',
    uf: 'UF',
    source: 'Origem',
    origem: 'Origem',
    tags: 'Tags',
    isActive: 'Status',
    subscribedAt: 'Data de Inscrição',
    unsubscribedAt: 'Data de Cancelamento',
    createdAt: 'Criado em',
    updatedAt: 'Atualizado em',
    id: 'ID'
  };

  constructor(
    private svc: NewsletterService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.loading = true;
    this.svc.list(this.page, this.pageSize, this.isActiveFilter).subscribe({
      next: (res: any) => {
        this.items = res?.data || [];
        this.totalPages = res?.meta?.totalPages || 1;
        this.total = res?.meta?.total || 0;
        if (this.items.length > 0) {
          this.columns = this.buildColumns(this.items[0]);
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('[NewsletterList] erro ao carregar', err);
        this.notification.showError('Erro ao carregar inscrições');
        this.loading = false;
      }
    });
  }

  setFilter(isActive: boolean | undefined) {
    this.isActiveFilter = isActive;
    this.page = 1;
    this.load();
  }

  filterBtnClass(value: boolean | undefined) {
    return this.isActiveFilter === value ? 'bg-primary text-white border-primary' : 'bg-white';
  }

  prev() {
    if (this.page > 1) {
      this.page--;
      this.load();
    }
  }

  next() {
    if (this.page < this.totalPages) {
      this.page++;
      this.load();
    }
  }

  unsubscribe(item: NewsletterSubscription) {
    if (!confirm(`Desinscrever ${item.email}?`)) return;
    this.svc.unsubscribe(item.email).subscribe({
      next: () => {
        this.notification.showSuccess('Inscrição cancelada');
        this.load();
      },
      error: (err) => {
        console.error('[NewsletterList] erro ao desinscrever', err);
        this.notification.showError('Erro ao cancelar inscrição');
      }
    });
  }

  resubscribe(item: NewsletterSubscription) {
    if (!confirm(`Reativar inscrição de ${item.email}?`)) return;
    this.svc.subscribe(item.email).subscribe({
      next: () => {
        this.notification.showSuccess('Inscrição reativada');
        this.load();
      },
      error: (err) => {
        console.error('[NewsletterList] erro ao reativar', err);
        this.notification.showError('Erro ao reativar inscrição');
      }
    });
  }

  private buildColumns(item: any): string[] {
    const knownOrder = [
      'email',
      'nome',
      'name',
      'firstName',
      'lastName',
      'phone',
      'telefone',
      'celular',
      'cidade',
      'estado',
      'uf',
      'source',
      'origem',
      'tags',
      'isActive',
      'subscribedAt',
      'unsubscribedAt',
      'createdAt',
      'updatedAt',
      'id'
    ];

    const keys = Object.keys(item || {});
    const ordered: string[] = [];

    knownOrder.forEach(k => {
      if (keys.includes(k)) ordered.push(k);
    });

    keys.forEach(k => {
      if (!ordered.includes(k)) ordered.push(k);
    });

    return ordered;
  }

  formatValue(item: any, key: string): string {
    const value = item?.[key];
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Ativo' : 'Inativo';
    if (typeof value === 'number') return value.toString();
    const str = String(value);
    // tenta formatar datas ISO
    const maybeDate = new Date(str);
    if (!isNaN(maybeDate.getTime()) && /\d{4}-\d{2}-\d{2}T/.test(str)) {
      return maybeDate.toLocaleString('pt-BR');
    }
    return str;
  }

  headerLabel(key: string): string {
    if (this.headerMap[key]) return this.headerMap[key];
    return key.charAt(0).toUpperCase() + key.slice(1);
  }
}
